/**
 * <StudentQuestDetailPage> — le détail d'une quête (page).
 *
 * Une page poussée depuis « Mes quêtes », comme aujourd'hui : la flèche y ramène. Son titre est le
 * nom de la quête (« Concentration »). Ce que l'ancien écran montre dans un carrousel à cinq
 * flèches, une étape par page, se lit ici d'un seul regard :
 *  - l'étape où l'élève en est, en grand : sa `<QuestCard>`, avec celui qui porte la quête à sa
 *    plus grande taille (72), la phrase, la barre, et ce que l'étape rapporte ;
 *  - « Pas encore débloquées » : les étapes à venir, avec leurs pétales et le titre qu'elles
 *    donnent ;
 *  - « Terminées » : celles qui sont faites.
 *
 * `composition="path"` est la variante en un seul chemin : toutes les étapes dans l'ordre, celle
 * en cours repérée au milieu des autres.
 *
 * Les étapes arrivent déjà rangées (faites, à venir) : c'est le view-model qui les sépare.
 * Rien ne presse : une étape ne se « lance » pas, elle avance quand l'élève travaille.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { IconButton } from "../../atoms/Buttons/IconButton"
import { columnStyles } from "../../atoms/containers/columns"
import { Stack } from "../../atoms/containers/Stack"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { CardList } from "../../molecules/CardList"
import { EmptyState } from "../../molecules/EmptyState"
import { Header } from "../../molecules/Header"
import { QuestStepRow, type UIQuestStep } from "../../molecules/QuestStepRow"
import { QuestCard, QuestChampion, type UIQuest } from "../../organisms/QuestCard"
import { PageTemplate, PARENT_CONTENT_WIDTH } from "../../templates/PageTemplate"
import { QUEST_DETAIL_TEXTS } from "./questDetailTexts"
import { StudentQuestDetailSkeleton } from "./StudentQuestDetailSkeleton"
import { type UIQuestDetail } from "../../../ports/primary/ui/types"

export type { UIQuestDetail }

export type StudentQuestDetailComposition = "groups" | "path"

export type StudentQuestDetailPageProps = {
    /** `null` tant que la quête n'est pas là, ou quand elle n'existe pas. */
    detail: UIQuestDetail | null
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    /** La flèche de l'en-tête : retour à « Mes quêtes ». */
    onBack: () => void
    /** `groups` (défaut) : en cours, à venir, terminées. `path` : toutes les étapes dans l'ordre. */
    composition?: StudentQuestDetailComposition
    testID?: string
}

/** L'étape en cours, redite en ligne dans le chemin. */
function currentStepOf(quest: UIQuest): UIQuestStep {
    return {
        step: quest.step,
        description: quest.description,
        status: quest.status === "done" ? "done" : "current",
        petals: quest.petals,
        titleGained: quest.titleGained,
    }
}

function Steps(props: { title?: string; steps: UIQuestStep[] }) {
    const { title, steps } = props
    if (steps.length === 0) return null

    return (
        <Stack gap="2">
            {title != null && <SectionLabel>{title}</SectionLabel>}
            <CardList>
                {steps.map((step) => (
                    <QuestStepRow key={step.step} step={step} />
                ))}
            </CardList>
        </Stack>
    )
}

export function StudentQuestDetailPage(props: StudentQuestDetailPageProps) {
    const { detail, loading = false, error, onRetry, onBack, composition = "groups", testID } = props

    const header = (
        <Header
            title={detail?.name ?? QUEST_DETAIL_TEXTS.fallbackTitle}
            left={
                <IconButton
                    iconName="chevron-left"
                    appearance="transparent"
                    iconColor="primary"
                    onPress={onBack}
                    accessibilityLabel="Retour"
                />
            }
        />
    )

    const body = () => {
        if (detail == null) return <EmptyState iconName="flag" title={QUEST_DETAIL_TEXTS.missing} />

        const { quest, doneSteps, lockedSteps } = detail
        const hero = <QuestCard quest={quest} champion={<QuestChampion name={quest.champion} hero />} />

        if (composition === "path") {
            return (
                <View style={[columnStyles.columns, styles.fullWidth]}>
                    <Stack gap="4" style={columnStyles.column}>
                        {hero}
                    </Stack>
                    <Stack gap="4" style={columnStyles.column}>
                        <Steps steps={[...doneSteps, currentStepOf(quest), ...lockedSteps]} />
                    </Stack>
                </View>
            )
        }

        // Au large, l'étape en cours fait face au reste du chemin : ce qui vient, puis ce qui est fait.
        return (
            <View style={[columnStyles.columns, styles.fullWidth]}>
                <Stack gap="4" style={columnStyles.column}>
                    {hero}
                </Stack>
                <Stack gap="4" style={columnStyles.column}>
                    <Steps title={QUEST_DETAIL_TEXTS.locked} steps={lockedSteps} />
                    <Steps title={QUEST_DETAIL_TEXTS.done} steps={doneSteps} />
                </Stack>
            </View>
        )
    }

    return (
        <PageTemplate
            testID={testID}
            header={header}
            loading={loading}
            skeleton={<StudentQuestDetailSkeleton />}
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={PARENT_CONTENT_WIDTH}
        >
            {body()}
        </PageTemplate>
    )
}

const styles = StyleSheet.create(() => ({
    /** La largeur de la colonne appartient à la page (`PageTemplate maxContentWidth`). */
    fullWidth: { width: "100%" },
}))
